if (!customElements.get('cart-page-recommendations')) {
  class CartPageRecommendations extends HTMLElement {
    connectedCallback() {
      this._grid = this.querySelector('.kt-section__inner > .kt-grid');
      if (!this._grid) return;

      const emptyTemplate = this.querySelector('[data-cart-recommendations-empty-products]');
      this._fallbackProducts = Array.from(emptyTemplate?.content.children || [], card => card.cloneNode(true));
      this._fallbackKey = JSON.parse(this.dataset.productIds || '[]').map(String).sort().join(',');
      emptyTemplate?.remove();
      this._refresh = () => {
        clearTimeout(this._refreshTimer);
        this._requestController?.abort();
        this._refreshTimer = setTimeout(() => this._load(), 50);
      };

      document.addEventListener('cart-updated', this._refresh, true);
      document.addEventListener('krown:cart:add', this._refresh);
      this._cartObserver = new MutationObserver(this._refresh);
      const cartForm = document.querySelector('.kt-cart-section cart-form');
      if (cartForm) this._cartObserver.observe(cartForm, { childList: true });

      this._load(JSON.parse(this.dataset.productIds || '[]'));
    }

    disconnectedCallback() {
      clearTimeout(this._refreshTimer);
      this._requestController?.abort();
      this._cartObserver?.disconnect();
      document.removeEventListener('cart-updated', this._refresh, true);
      document.removeEventListener('krown:cart:add', this._refresh);
    }

    async _load(initialProductIds) {
      this._requestController?.abort();
      const controller = new AbortController();
      this._requestController = controller;
      this.setAttribute('aria-busy', 'true');

      try {
        let productIds = initialProductIds;
        if (!productIds) {
          const response = await fetch(this.dataset.cartUrl, {
            signal: controller.signal,
            cache: 'no-store'
          });
          if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
          const cart = await response.json();
          productIds = cart.items.map(item => item.product_id);
        }

        productIds = [...new Set(productIds.map(String))];
        const cartKey = [...productIds].sort().join(',');
        if (controller.signal.aborted || this._renderedKey === cartKey) return;

        const excludedIds = new Set(productIds);
        const limit = Number(this.dataset.limit) || 4;
        const fallbackCards = () => this._fallbackProducts
          .filter(card => !excludedIds.has(card.dataset.cartRecommendationProduct))
          .slice(0, limit)
          .map(card => card.cloneNode(true));
        let requestFailed = false;
        const refreshFallback = async () => {
          try {
            await this._loadFallback(cartKey, controller.signal);
          } catch (error) {
            if (error.name === 'AbortError') throw error;
            requestFailed = true;
          }
        };

        this._render(fallbackCards(), undefined, !productIds.length);

        if (!productIds.length) {
          await refreshFallback();
          if (controller.signal.aborted) return;
          this._render(fallbackCards(), requestFailed ? undefined : cartKey, true);
          return;
        }

        const products = [];

        for (const productId of productIds) {
          const url = new URL(this.dataset.recommendationsUrl, window.location.origin);
          const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');
          if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);
          url.searchParams.set('section_id', this.dataset.sectionId);
          url.searchParams.set('product_id', productId);
          url.searchParams.set('limit', '10');
          url.searchParams.set('intent', 'related');

          try {
            const response = await fetch(url, { signal: controller.signal });
            if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);
            const html = new DOMParser().parseFromString(await response.text(), 'text/html');
            const recommendations = html.querySelector('cart-page-recommendations[data-performed="true"]');
            if (!recommendations) continue;

            for (const card of recommendations.querySelectorAll('[data-cart-recommendation-product]')) {
              const recommendationId = card.dataset.cartRecommendationProduct;
              if (!excludedIds.has(recommendationId)) {
                excludedIds.add(recommendationId);
                products.push(document.importNode(card, true));
              }
              if (products.length >= limit) break;
            }
          } catch (error) {
            if (error.name === 'AbortError') throw error;
            requestFailed = true;
          }

          if (controller.signal.aborted) return;
          if (products.length >= limit) break;
        }

        if (products.length < limit) {
          await refreshFallback();
          if (controller.signal.aborted) return;
          for (const card of fallbackCards()) {
            excludedIds.add(card.dataset.cartRecommendationProduct);
            products.push(card);
            if (products.length >= limit) break;
          }
        }

        this._render(products, requestFailed ? undefined : cartKey);
      } catch (error) {
        if (error.name !== 'AbortError' && !controller.signal.aborted) {
          this.hidden = true;
          this._grid.replaceChildren();
          this._renderedKey = undefined;
        }
      } finally {
        if (this._requestController === controller) {
          this.removeAttribute('aria-busy');
          this._requestController = null;
        }
      }
    }

    async _loadFallback(cartKey, signal) {
      if (this._fallbackKey === cartKey) return;
      // Re-render only a row's worth of collection cards after cart contents change.
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', this.dataset.sectionId);
      const response = await fetch(url, { signal, cache: 'no-store' });
      if (!response.ok) throw new Error(`Fallback request failed: ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const template = html.querySelector('[data-cart-recommendations-empty-products]');
      if (!template) throw new Error('Fallback products are missing');
      if (signal.aborted) return;
      this._fallbackProducts = Array.from(template.content.children, card => document.importNode(card, true));
      this._fallbackKey = cartKey;
    }

    _render(cards, cartKey, emptyCart = false) {
      this._grid.replaceChildren(...cards);
      this._renderedKey = cartKey;
      this.hidden = !cards.length;
      this.querySelector('[data-cart-recommendations-heading]').hidden = emptyCart;
      this.querySelector('[data-cart-recommendations-empty-heading]').hidden = !emptyCart;

      if (cards.length) {
        this.querySelectorAll('form').forEach(form => {
          const template = form.querySelector('template');
          if (template) form.append(template.content.cloneNode(true));
        });
        window.KrownAssetLoader?.loadSectionAssets(this.dataset.sectionId);
        this.dispatchEvent(new Event('product-recommendations-loaded'));
      }
    }
  }

  customElements.define('cart-page-recommendations', CartPageRecommendations);
}
